import type { EncashmentRecord } from '@/app/features/employee/emp-payroll';
import type { TableProps } from 'antd';
import { formatBDT } from '../shared/payroll.helpers';

/**
 * Reading encashment records. Every record lists its own leave types, so the
 * table columns are worked out from all records on the page.
 */

/** Only a PENDING record can still be edited; the server refuses the rest. */
export const isRecordEditable = (record?: EncashmentRecord | null) =>
  record?.status === 'PENDING';

/** Every leave type that appears on the page, in first-seen order. */
const leaveTypesOf = (records: EncashmentRecord[]) => {
  const types = new Map<string, string>();
  records.forEach((record) =>
    record.encashment_breakdown?.forEach((item) => {
      if (!types.has(item.leave_type_id)) {
        types.set(item.leave_type_id, item.leave_name);
      }
    }),
  );
  return [...types].map(([id, name]) => ({ id, name }));
};

const nowrap = (title: string) => (
  <span className="whitespace-nowrap">{title}</span>
);

/** The employee columns both record tables start with. */
export const employeeColumns: TableProps<EncashmentRecord>['columns'] = [
  {
    title: nowrap('Employee ID'),
    dataIndex: ['employee', 'employee_id'],
    render: (val: string) => val || 'n/a',
  },
  {
    title: nowrap('Employee Name'),
    dataIndex: ['employee', 'name'],
    render: (val: string) => val || 'n/a',
  },
  {
    title: nowrap('Department'),
    dataIndex: ['employee', 'department', 'name'],
    render: (val?: string) => val ?? 'n/a',
  },
];

/** One amount column per leave type; a record without that leave shows 0. */
export const leaveColumns = (
  records: EncashmentRecord[],
): TableProps<EncashmentRecord>['columns'] =>
  leaveTypesOf(records).map((leave) => ({
    key: leave.id,
    title: nowrap(leave.name),
    align: 'center' as const,
    render: (_: unknown, record: EncashmentRecord) =>
      formatBDT(
        record.encashment_breakdown?.find(
          (item) => item.leave_type_id === leave.id,
        )?.amount ?? 0,
      ),
  }));
